import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import { useSelector } from '@xstate/react';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ActorRefFrom, waitFor } from 'xstate';

import { useApiClient } from '@eluve/api-client-provider';
import { SessionMachine } from '@eluve/apollo-client';
import { FCC, useToast } from '@eluve/components';
import { LoggerArgs, useExtendedLogger } from '@eluve/logger';
import { useSession, useTenantIdFromParams } from '@eluve/session-helpers';

/**
 * Upper bound on how long to hold children back waiting for a valid token for
 * the current tenant. The happy path resolves in well under a second; this only
 * exists so a wedged auth state degrades to "render anyway" instead of a
 * permanent blank screen [ENG-4408].
 */
const TENANT_GATE_TIMEOUT_MS = 15_000;

// Bounded retry for the stale-access recheck. Thrown refresh = transient
// transport failure, not "no access" — retry with backoff. Resolved (201/403)
// is final, not retried.
const ACCESS_RECHECK_RETRY = {
  maxAttempts: 3,
  baseDelayMs: 500,
  maxDelayMs: 4000,
} as const;

export const TenantGuard: FCC<{
  client: ApolloClient<NormalizedCacheObject>;
  sessionActor: ActorRefFrom<SessionMachine>;
}> = ({ children, client, sessionActor }) => {
  const [machineInitialized, setMachineInitialized] = useState(false);
  // Recheck outcome when the claim says not provisioned (claim can be stale —
  // see refreshStaleTenantAccess). 'verified' = server rebuilt the claim from
  // the DB and the tenant is still missing (genuinely not a member); 'failed' =
  // no verdict (transport error / 403 edge), a transient failure that must not
  // be presented as a permanent denial.
  //
  // Keyed by tenantId: this guard instance is reused across client-side tenant
  // changes (A→B, no remount). A verdict computed for A must not be acted on
  // during B's first render — see the `recheckStatus` derivation below.
  // [ENG-4763]
  const [accessRecheck, setAccessRecheck] = useState<{
    tenantId: string | null;
    status: 'idle' | 'refreshing' | 'verified' | 'failed';
  }>({ tenantId: null, status: 'idle' });
  const toast = useToast();

  const apiClient = useApiClient();
  const tenantSwitchFailed = useSelector(sessionActor, (state) =>
    state.matches('TenantSwitchFailed'),
  );

  const { pathname } = useLocation();
  const { appointmentId } = useParams<{ appointmentId?: string }>();
  const redirectToDefaultTenant = pathname.includes('/admin') ? '/admin' : '/';

  const tenantParamsId = useTenantIdFromParams();

  const allowedTenants = useSession().allowedTenantIds;
  const tenantLoggerArgs: LoggerArgs = useMemo(
    () => ({ name: 'TenantGuard', metadata: { tenantId: tenantParamsId } }),
    [tenantParamsId],
  );

  const logger = useExtendedLogger(tenantLoggerArgs);

  useEffect(
    // We don't want to mount the ApolloClient (and render children) until the
    // session machine has acquired a usable token for the *current* tenant —
    // not merely acknowledged the desired tenant. The old `useMount` +
    // `desiredTenantId` check resolved the instant `CHANGE_TENANT` was
    // processed, before `TokenAvailable`, so children could render and fire
    // requests with a stale/empty JWT (→ 404 "Appointment not found"). It also
    // only ran once, so browser back/forward between tenants (which reuses this
    // component) left the gate permanently open [ENG-4408].
    //
    // Keying on `tenantParamsId` re-closes the gate on every tenant change; the
    // `cancelled` flag prevents a stale `waitFor` from a previous tenant from
    // re-opening it after the param changed.
    function gateOnTokenForCurrentTenant() {
      if (!tenantParamsId) {
        return;
      }

      let cancelled = false;
      setMachineInitialized(false);

      waitFor(
        sessionActor,
        (state) =>
          state.matches('TokenAvailable') &&
          state.context.desiredTenantId === tenantParamsId,
        { timeout: TENANT_GATE_TIMEOUT_MS },
      )
        .then(() => {
          if (!cancelled) {
            setMachineInitialized(true);
          }
        })
        .catch(() => {
          // Token never became available for this tenant within the timeout
          // (e.g. auth wedged). Fall back to rendering rather than leaving the
          // user on a permanent blank screen; a genuine tenant-switch failure
          // is handled reactively by the `tenantSwitchFailed` branch below.
          if (!cancelled) {
            setMachineInitialized(true);
          }
        });

      return () => {
        cancelled = true;
      };
    },
    [sessionActor, tenantParamsId],
  );

  if (!tenantParamsId) {
    throw new Error('Expected tenant id in route params in TenantGuard');
  }

  // allowedTenants is a fresh array on every render (useSession re-parses the
  // JWT each time), so derive a stable primitive before using it as an effect
  // dependency — depending on the array would re-run the effect every render
  // and re-send CHANGE_TENANT, which clears the JWT and forces a token re-fetch.
  const isProvisionedForTenant = allowedTenants.includes(tenantParamsId);

  // A recheck verdict only applies to the tenant it was computed for. When the
  // route param changes client-side (no remount), the leftover verdict from the
  // previous tenant reads as 'idle' so we hold and re-verify the new tenant
  // instead of redirecting on a stale verdict. [ENG-4763]
  const recheckStatus =
    accessRecheck.tenantId === tenantParamsId ? accessRecheck.status : 'idle';

  useEffect(
    // Provide new values to the session management machine so that
    // it can reconcile to the desired tenant
    function updateTenantIdForSession() {
      // NOTE: this session-reconciliation effect is mirrored in
      // libs/frontend/feature/admin/src/lib/TenantGuard.tsx — keep the
      // isProvisionedForTenant guard below in sync across both copies.
      //
      // Don't attempt a token/tenant switch on the redirect path. When the user
      // isn't provisioned for tenantParamsId, the render-time guard below bounces
      // them away. Sending CHANGE_TENANT here would drive the session machine
      // through a 403 switch-tenant call into TenantSwitchFailed, which then
      // breaks the Apollo token fetch on the destination page.
      if (!isProvisionedForTenant) return;

      sessionActor.send({
        type: 'CHANGE_TENANT',
        desiredTenantId: tenantParamsId,
      });

      sessionActor.send({ type: 'SUPPLY_LOGGER', logger });

      sessionActor.send({
        type: 'SUPPLY_RESET_LOGIC',
        reset: async () => {
          const res = await apiClient.auth.createTenantSession({
            body: {
              tenantId: tenantParamsId,
            },
          });

          if (res.status !== 201) {
            throw new Error(
              `Failed to switch tenant session. Status: ${res.status}`,
            );
          }
        },
      });
    },
    [apiClient.auth, logger, tenantParamsId, isProvisionedForTenant],
  );

  useEffect(
    // Claim may be stale: another admin can add us (writing the tenant_users
    // row) without touching this session's token. Refresh allowedTenantIds —
    // runs in our session, rebuilds the claim from the DB. Success →
    // isProvisionedForTenant flips true; else fall through to the deny path.
    // Keyed on tenantParamsId not accessRecheck so the 'refreshing'
    // self-transition can't re-run the effect and cancel its own retry.
    function refreshStaleTenantAccess() {
      if (isProvisionedForTenant) return;

      let cancelled = false;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      setAccessRecheck({ tenantId: tenantParamsId, status: 'refreshing' });

      const attempt = (n: number): void => {
        // A retry scheduled before cleanup still fires after it; bail before
        // sending a request that would resolve against a stale tenant.
        if (cancelled) return;
        void apiClient.auth
          .refreshAllowedTenants({ body: {} })
          // Resolved = definitive. 201 = claim rebuilt ('verified'); other
          // status (403 edge) = no verdict ('failed').
          .then((res) => {
            if (!cancelled) {
              setAccessRecheck({
                tenantId: tenantParamsId,
                status: res.status === 201 ? 'verified' : 'failed',
              });
            }
          })
          // Thrown = transient transport failure; retry with backoff, else fail.
          .catch(() => {
            if (cancelled) return;
            if (n < ACCESS_RECHECK_RETRY.maxAttempts) {
              const delay = Math.min(
                ACCESS_RECHECK_RETRY.baseDelayMs * Math.pow(2, n - 1),
                ACCESS_RECHECK_RETRY.maxDelayMs,
              );
              retryTimer = setTimeout(() => attempt(n + 1), delay);
            } else {
              setAccessRecheck({ tenantId: tenantParamsId, status: 'failed' });
            }
          });
      };

      attempt(1);

      // Cancel pending retries on unmount/tenant change so a stale chain can't
      // resolve against another tenant, and clear any scheduled retry so it
      // can't fire a request after this effect is torn down.
      return () => {
        cancelled = true;
        if (retryTimer) clearTimeout(retryTimer);
      };
    },
    [apiClient.auth, isProvisionedForTenant, tenantParamsId],
  );

  useEffect(function transitionToIdleSessionOnUnmount() {
    return () => {
      sessionActor.send({ type: 'SESSION_COMPONENT_UNMOUNTED' });
    };
  }, []);

  useEffect(
    // A tenant switch fails server-authoritatively (403) when the user's
    // membership was revoked but the front-token's allowedTenantIds claim is
    // still stale. A *failed* switch does not rebuild the claim (the server
    // throws before re-minting the token), so the revoked tenant would keep
    // appearing as switchable until the next ~95s token refresh. Rebuild the
    // claim now so it drops out immediately. [ENG-4763]
    function selfHealClaimOnSwitchFailure() {
      if (!tenantSwitchFailed) return;

      // Best-effort: the claim still self-heals on the next token refresh.
      void apiClient.auth
        .refreshAllowedTenants({ body: {} })
        .catch(() => undefined);
    },
    [tenantSwitchFailed, apiClient.auth],
  );

  if (!isProvisionedForTenant) {
    // Still verifying — hold rendering, don't treat a stale token as denial.
    if (recheckStatus === 'idle' || recheckStatus === 'refreshing') {
      return null;
    }

    // No verdict (transport failure / 403 edge): the claim was not
    // authoritatively refreshed, so this is not a confirmed denial. Show an
    // honest retry hint and dead-end to the default tenant rather than
    // mislabeling a transient transport failure as "Access denied". Mirrors the
    // admin guard's failed branch. [ENG-4763]
    if (recheckStatus === 'failed') {
      toast.error('Error', {
        id: `tenant-access-recheck-failed-${tenantParamsId}`,
        description:
          'Unable to verify access to that organization. Please try again.',
      });

      return <Navigate to={redirectToDefaultTenant} />;
    }

    // 'verified': claim rebuilt and the tenant is still missing → genuinely not
    // a member.
    let redirectTo = redirectToDefaultTenant;

    if (appointmentId) {
      redirectTo = `/admin/organizations?goToAppointmentId=${appointmentId}`;
    } else {
      toast.error('Error', {
        id: 'access-denied',
        description: 'Access denied',
      });
    }

    return <Navigate to={redirectTo} />;
  }

  if (tenantSwitchFailed) {
    toast.error('Error', {
      id: `tenant-switch-failed-${tenantParamsId}`,
      description: 'Unable to switch to that organization',
    });

    return <Navigate to={redirectToDefaultTenant} />;
  }

  // Wait for the session machine to be in a state where it has
  // acknowledged the correct tenant ID
  if (!machineInitialized) {
    return null;
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
